import { AfterViewInit, ChangeDetectorRef, Component, ElementRef, OnDestroy, OnInit, ViewChild } from '@angular/core';
import { ActivatedRoute, NavigationEnd, Router } from '@angular/router';
import { select, Store } from '@ngrx/store';
import { BrowserTabTitleService } from 'core/services/browser-tab-title.service';
import { SearchResultHelper } from 'core/utils/search-result-helper';
import { Subscription } from 'rxjs';
import { delay, filter } from 'rxjs/operators';
import { ScrollService } from 'search/full-card/services/scroll-service';
import { BookCover, SelectedRollUpTab } from 'search/models/search-results';
import { getIsFirstPage } from 'search/reducers/history.reducer';
import { tryToGetItRollup, tryToPlaceHoldOnRollup } from '../../../entity/actions/entity.actions';
import {
  AgentOrConceptData,
  Entity,
  EntityTypes,
  FormatGroup,
  GetItRequest,
  HoldRequest
} from '../../../entity/models/entity';
import { getSelectedRollupTab } from '../../../entity/reducers/entity.reducer';
import { selectEntity } from '../../../entity/reducers/full-entity.reducer';
import { ShowcasesState } from '../reducers/showcases.reducer';

@Component({
  selector: 'app-card-container',
  templateUrl: './card-container.component.html',
  styleUrls: ['./card-container.component.scss'],
})
export class CardContainerComponent implements OnInit, OnDestroy, AfterViewInit {
  public data: Entity | FormatGroup;
  public entity: AgentOrConceptData | FormatGroup;
  public type: string;
  public hasPreviousUrl: boolean;
  public entityType: string;
  public useHeaderV2 = false;
  @ViewChild('card') public card: ElementRef;

  private readonly subscriptions: Subscription = new Subscription();
  private tableExpanded = false;
  private tableOpenedOnce = false;

  constructor(
    private readonly store: Store<ShowcasesState>,
    public searchResultHelper: SearchResultHelper,
    private readonly cdRef: ChangeDetectorRef,
    public router: Router,
    public route: ActivatedRoute,
    public scrollService: ScrollService,
    private readonly titleService: BrowserTabTitleService
  ) {
  }

  public onTableInited() {
    if (this.tableExpanded && !this.tableOpenedOnce) {
      this.card.nativeElement.querySelector('app-location-and-edition-table').scrollIntoView();
      this.tableOpenedOnce = true;
    }
  }

  public ngOnInit() {
    this.subscriptions.add(this.router.events.pipe(filter((event) => event instanceof NavigationEnd))
      .subscribe(() => {
        this.card?.nativeElement?.focus();
      }));

    this.subscriptions.add(this.store.pipe(select(getIsFirstPage)).subscribe((isFirstPage: boolean) => {
      this.hasPreviousUrl = !isFirstPage;
    }));

    this.subscriptions.add(
      this.store.pipe(select(selectEntity))
        .subscribe((data) => {
          this.data = data;
          if (data) {
            this.entity = this.extractEntity(data);
            this.cdRef.markForCheck();

            if (this.data.id) {
              this.subscriptions.add(this.store.pipe(select(getSelectedRollupTab(this.data.id)))
                .subscribe((selected: SelectedRollUpTab) => {
                  this.tableExpanded = selected && selected.expandLocationAndEditionInformation;
                }));
            }
          }
        }));
  }

  public ngAfterViewInit() {
    this.subscriptions.add(this.scrollService.isShouldScroll$.pipe(
      delay(100),
    ).subscribe(() => {
      this.scrollToContent();
    }));
  }

  public extractEntity(data: Entity | FormatGroup): AgentOrConceptData | FormatGroup {
    this.useHeaderV2 = false;
    switch (data.entityType) {
      case 'Agent':
        return this.extractAgentData();
      case 'Concept':
        return this.extractConceptData();
      case 'FormatGroup':
        return this.extractFormatGroupData();
      default:
        return null;
    }
  }

  public extractFormatGroupData(): FormatGroup {
    this.useHeaderV2 = true;
    const fg = this.data as FormatGroup;
    const title = fg.title;
    const author = fg?.primaryAgent?.label;
    this.titleService.setTabTitle(`${title}${author ? ', ' + author : ''}`);
    return fg;
  }

  public extractAgentData(): AgentOrConceptData {
    const agentData = this.data as Entity;
    const title = agentData.label;
    const coverConfig: BookCover = {
      type: EntityTypes.AGENT,
      coverUrl: this.data.coverUrl,
    };
    this.titleService.setTabTitle(title);

    return {
      type: agentData.type,
      format: agentData.type,
      title,
      hasAssociations: true,
      id: agentData.id,
      entityType: agentData.entityType,
      coverConfig,
      materialTypeLabel: this.searchResultHelper.extractMaterialTypeDescription(agentData),
      bookIsbn: agentData.bookIsbn,
    };
  }

  public extractConceptData(): AgentOrConceptData {
    const conceptData = this.data as Entity;
    const title = conceptData.label;
    const coverConfig: BookCover = {
      type: EntityTypes.CONCEPT,
    };
    this.titleService.setTabTitle(title);

    return {
      type: conceptData.type,
      format: 'concept',
      title,
      description: {
        label: this.searchResultHelper.extractDescriptionDataByType('label', conceptData),
        source: this.searchResultHelper.extractDescriptionDataByType('source', conceptData),
        url: this.searchResultHelper.extractDescriptionDataByType('url', conceptData),
      },
      hasAssociations: true,
      id: conceptData.id,
      entityType: conceptData.entityType,
      coverConfig,
      materialTypeLabel: this.searchResultHelper.extractMaterialTypeDescription(conceptData),
    };
  }

  public ngOnDestroy() {
    this.subscriptions.unsubscribe();
  }

  public getItForRollupCard(data: {item: GetItRequest}) {
    const payload = {
      item: data.item,
    };
    this.store.dispatch(tryToGetItRollup(payload));
  }

  public placeHoldOnRollupCard(data: {item: HoldRequest}) {
    const payload = {
      item: data.item,
    };
    this.store.dispatch(tryToPlaceHoldOnRollup(payload));
  }

  public scrollToContent() {
    this.card.nativeElement.querySelector('.card-body')?.scrollIntoView();
  }
}
